import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  Grid,
  Button,
  Backdrop,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Fade,
  Stack,
  Divider,
  Container,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  IconButton,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import RedeemIcon from '@mui/icons-material/Redeem';
import FastfoodIcon from '@mui/icons-material/Fastfood';
import RefreshIcon from '@mui/icons-material/Refresh';
import CloseIcon from '@mui/icons-material/Close';
import * as XLSX from 'xlsx';
import { useCorporateMultipleSendTypesQuery, useGetBalanceForSendMoneyQuery, useGetListCommissionValueMutation, useSendCashbackListMutation, useSendMealAllowanceListAsyncMutation, useSendMoneyListMutation, useSendMoneyPhoneNumberListMutation } from './services/services';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/store';
import { enqueueSnackbar } from 'notistack';
import { useMultiplePermissions } from '../../hooks/usePermission';
import FintechLoader from '../../components/FintechLoader';
import { validatePhoneNumber } from '../../utils/validata-iban-tckn';
import {
  Security as SecurityIcon,
} from '@mui/icons-material';

const fileFormats = ['.xlsx', '.cvs', '.xls', '.xlsm', '.xlsb', '.xlt'];

const BulkTransferPage: React.FC = () => {
  const { currentUserState } = useSelector((state: RootState) => state.currentUserState);
  const [spinner, setSpinner] = useState(false);
  const [selectedTransactionType, setSelectedTransactionType] = useState<string>('1');
  const [sendMoneyAgainControl, setSendMoneyAgainControl] = useState(false);
  const [isShowSendMoneyButton, setIsShowSendMoneyButton] = useState(true);
  const [listOfData, setListOfData] = useState<any[]>([]);
  const [commission, setCommission] = useState<any>({});
  const [totalValueTL, setTotalValueTL] = useState(0);
  const [totalValueUSD, setTotalValueUSD] = useState(0);
  const [totalValueEURO, setTotalValueEURO] = useState(0);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState<'success' | 'warning' | 'error'>('success');
  const [messageDescription, setMessageDescription] = useState('');
  const [errorRows, setErrorRows] = useState<any[]>([]);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { data: corporateSendTypes, isLoading: corporateSendTypesLoading } = useCorporateMultipleSendTypesQuery({ companyId: currentUserState.companyId }, { skip: !currentUserState.companyId });
  const [getListCommissionValue, { isLoading: isCommissionLoading }] = useGetListCommissionValueMutation();
  const [sendMoneyList] = useSendMoneyListMutation();
  const [sendMoneyPhoneNumberList] = useSendMoneyPhoneNumberListMutation();
  const [sendMealAllowanceListAsync] = useSendMealAllowanceListAsyncMutation();
  const [sendCashbackList] = useSendCashbackListMutation();
  const { data: balanceData, isLoading: balanceLoading } = useGetBalanceForSendMoneyQuery({ companyId: currentUserState?.companyId }, { skip: !currentUserState.companyId });
  const permissionCodes = [
    'Corporate.POST.Reading.CüzdandanCüzdanaTopluParaTransferi',
    'Corporate.POST.Reading.HesaptanTelefonNumarasınaTopluParaTransferi',
  ];
  const permissions = useMultiplePermissions(permissionCodes);
  const canBulkTransferFromWallet = permissions['Corporate.POST.Reading.CüzdandanCüzdanaTopluParaTransferi']?.hasPermission;
  const permissionLoading = permissions['Corporate.POST.Reading.CüzdandanCüzdanaTopluParaTransferi']?.isLoading;
  const canBulkTransferFromAccountToPhone = permissions['Corporate.POST.Reading.HesaptanTelefonNumarasınaTopluParaTransferi']?.hasPermission;
  const phoneTransferPermissionLoading = permissions['Corporate.POST.Reading.HesaptanTelefonNumarasınaTopluParaTransferi']?.isLoading;
  const [loaderTrigger, setLoaderTrigger] = useState(false);
  // Ana yükleme durumu - tüm kritik veriler yüklenene kadar bekle
  const isInitialLoading = permissionLoading || phoneTransferPermissionLoading || corporateSendTypesLoading;

  const formatAndDisplayValue = (value: number, currency: string) => {
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
    }).format(value);
  };

  const formatCurrency = (value: string, currency: string) => {
    if (value != null && currency != null) {
      const currencyCode = getProperCurrencyCode(currency);
      return new Intl.NumberFormat('tr-TR', {
        style: 'currency',
        currency: currencyCode,
        minimumFractionDigits: 2,
      }).format(parseFloat(value));
    }
    return '';
  };

  const getProperCurrencyCode = (currency: string) => {
    switch (currency) {
      case '1':
      case 'TL':
        return 'TRY';
      case '2':
      case 'USD':
        return 'USD';
      case '3':
      case 'EUR':
        return 'EUR';
      default:
        return 'TRY';
    }
  };

  const getCurrency = (data: string) => {
    if (data === 'USD') return '2';
    if (data === 'EUR') return '3';
    if (data === 'TL') return '1';
    return '1';
  };

  const getReverseCurrency = (data: string) => {
    if (data === '2') return 'USD';
    if (data === '3') return 'EUR';
    if (data === '1') return 'TL';
    return 'TL';
  };

  const calculateTotalValue = (arr: any[], currency: string) => {
    let totalValue = 0;
    for (const item of arr) {
      if (item.currencyId === currency) {
        totalValue += parseFloat(item.value);
      }
    }
    return parseFloat(totalValue.toFixed(2));
  };

  // Excel config - Hem boşluklu hem boşluksuz başlıkları destekler
  const getExcelConfig = (transactionType: string) => {
    switch (transactionType) {
      case '1':
        return {
          requiredProperties: ['Açıklama', 'Alıcı Hesap No', 'Para Birimi', 'Tutar'],
          receiverField: 'Alıcı Hesap No'
        };
      case '48':
        return {
          requiredProperties: ['Açıklama', 'Cep Telefonu', 'Para Birimi', 'Tutar'],
          receiverField: 'Cep Telefonu'
        };
      case '7':
        return {
          requiredProperties: ['Açıklama', 'Alıcı Hesap No', 'Para Birimi', 'Tutar'],
          receiverField: 'Alıcı Hesap No'
        };
      case '36':
        return {
          requiredProperties: ['Açıklama', 'Alıcı Hesap No', 'Para Birimi', 'Tutar'],
          receiverField: 'Alıcı Hesap No'
        };
      default:
        return {
          requiredProperties: ['Açıklama', 'Alıcı Hesap No', 'Para Birimi', 'Tutar'],
          receiverField: 'Alıcı Hesap No'
        };
    }
  };

  // File download
  const downloadSampleFile = () => {
    let downloadFileName = 'OrnekFormat.xlsx';
    let excelFilePath: string = '/assets/data/TopluParaGonder.xlsx';

    switch (selectedTransactionType) {
      case '1':
        downloadFileName = 'HesaplaraParaGonder.xlsx';
        excelFilePath = '/assets/data/TopluParaGonder.xlsx';
        break;
      case '48':
        downloadFileName = 'TelefonNumarasıylaParaGonder.xlsx';
        excelFilePath = '/assets/data/TelefonNumarasıylaParaGonder.xlsx';
        break;
      case '7':
        downloadFileName = 'CashbackGonder.xlsx';
        break;
      case '36':
        downloadFileName = 'YemekKartıTopluParaYukle.xlsx';
        break;
      default:
        downloadFileName = 'OrnekFormat.xlsx';
        break;
    }
    // Just trigger download for demo
    const a = document.createElement('a');
    a.href = excelFilePath;
    a.download = downloadFileName;
    a.click();
  };

  // File upload
  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    // Önceki hata mesajlarını sıfırla
    setMessage('');
    setMessageDescription('');
    setMessageType('success');
    setErrorRows([]);
    setShowErrorModal(false);

    if (!event.target.files || event.target.files.length === 0) {
      setSpinner(false);
      return;
    }
    setSpinner(true);
    const selectedFile = event.target.files[0];
    if (fileFormats.some((format) => selectedFile.name.endsWith(format))) {
      const fileReader = new FileReader();
      fileReader.onload = async (e: any) => {
        const binaryData = e.target.result;
        const workbook = XLSX.read(binaryData, { type: 'binary' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        if (worksheet && worksheet['!ref']) {
          worksheet['!ref'] = worksheet['!ref'].replace('A1', 'A2');
        }
        const excelData: any[] = XLSX.utils.sheet_to_json(worksheet);
        const excelDataformat: any[] = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
        const excelConfig = getExcelConfig(selectedTransactionType);

        if (isSendMoneyArray(excelDataformat[0], excelConfig.requiredProperties)) {
          // Boş Excel kontrolü
          if (excelData.length === 0) {
            setSpinner(false);
            setMessageType('error');
            setMessage('Yüklediğiniz Excel dosyası boş.');
            setMessageDescription('Excel dosyasında en az bir satır veri bulunmalıdır.');
            fileInputRef.current!.value = '';
            return;
          }

          // Başlık normalizasyonu için yardımcı fonksiyon
          const normalizeHeaders = (data: any[]) => {
            const headerMap = new Map();

            // Orijinal başlıkları ve boşluksuz versiyonlarını haritala
            excelConfig.requiredProperties.forEach(prop => {
              const withoutSpaces = prop.replace(/\s+/g, '');
              headerMap.set(prop, prop);
              headerMap.set(withoutSpaces, prop);
            });

            return data.map(row => {
              const normalizedRow: any = {};
              Object.keys(row).forEach(key => {
                const normalizedKey = headerMap.get(key) || key;
                normalizedRow[normalizedKey] = row[key];
              });
              return normalizedRow;
            });
          };

          // Excel verilerini normalize et
          const normalizedExcelData = normalizeHeaders(excelData);

          const ListData: any[] = [];
          const errorRowsList: any[] = [];

          for (let i = 0; i < normalizedExcelData.length; i++) {
            const rowNumber = i + 2; // Excel'de başlık satırından sonra başlar
            const currentRow = normalizedExcelData[i];
            let hasError = false;
            let errorMessages: string[] = [];

            // Tutar kontrolü
            const valueControl = isNaN(currentRow.Tutar);
            if (valueControl || !currentRow.Tutar) {
              hasError = true;
              errorMessages.push('Tutar alanı geçerli değil veya boş');
            }

            // Alıcı alan kontrolü - Normalize edilmiş alan adını kullan
            const receiverValue = currentRow[excelConfig.receiverField];
            if (!receiverValue) {
              hasError = true;
              errorMessages.push(`${excelConfig.receiverField} alanı bulunamadı veya boş`);
            } else if (selectedTransactionType === '48') {
              // Telefon numarası formatı kontrolü (sadece telefon numarası ile para gönderme için)
              if (!validatePhoneNumber(receiverValue)) {
                hasError = true;
                errorMessages.push('Telefon numarası geçerli değil (905xxxxxxxxx formatında olmalı)');
              }
            }

            // Para birimi kontrolü - Normalize edilmiş alan adını kullan
            if (!currentRow['Para Birimi'] || !['TL', 'USD', 'EUR'].includes(currentRow['Para Birimi'])) {
              hasError = true;
              errorMessages.push('Para birimi geçerli değil (TL, USD, EUR olmalı)');
            }

            if (hasError) {
              errorRowsList.push({
                rowNumber,
                data: currentRow,
                errors: errorMessages,
                description: currentRow.Açıklama || '',
                receiver: receiverValue || '',
                currency: currentRow['Para Birimi'] || '',
                amount: currentRow.Tutar || ''
              });
            } else {
              let sendMoneyItem = {}
              if (selectedTransactionType === '48') {
                sendMoneyItem = {
                  description: currentRow.Açıklama || '',
                  receiverPhoneNumber: receiverValue.toString(),
                  currencyId: getCurrency(currentRow['Para Birimi']),
                  value: currentRow.Tutar,
                };

              } else {
                sendMoneyItem = {
                  description: currentRow.Açıklama || '',
                  receiverWalletNo: receiverValue.toString(),
                  currencyId: getCurrency(currentRow['Para Birimi']),
                  value: currentRow.Tutar,
                };
              }

              ListData.push(sendMoneyItem);
            }
          }

          // Hatalı satırlar varsa hata modalını göster
          if (errorRowsList.length > 0) {
            setErrorRows(errorRowsList);
            setShowErrorModal(true);
            setSpinner(false);
            setMessageType('warning');
            setMessage(`${errorRowsList.length} satırda hata bulundu.`);
            setMessageDescription('Detayları görmek için "Hataları Görüntüle" butonuna tıklayın.');
            fileInputRef.current!.value = '';
            return;
          }
          setTotalValueTL(calculateTotalValue(ListData, '1'));
          setTotalValueUSD(calculateTotalValue(ListData, '2'));
          setTotalValueEURO(calculateTotalValue(ListData, '3'));

          // Komisyon hesaplama API çağrısı
          await calculateCommissions(ListData);

          ListData.forEach((x) => {
            x.currencyId = getReverseCurrency(x.currencyId);
          });
          setListOfData(ListData);
          setSpinner(false);

          // Tek mesajda hem yükleme başarısını hem de satır sayısını göster
          enqueueSnackbar(`Excel başarıyla yüklendi. ${ListData.length} satır işlenmeye hazır.`, { variant: 'success' });
          setIsShowSendMoneyButton(true);
        } else {
          fileInputRef.current!.value = '';
          setSpinner(false);
          setIsShowSendMoneyButton(false);

          // İşlem tipine göre özelleştirilmiş hata mesajı
          const transactionTypeName = selectedTransactionType === '48' ? 'Telefon Numarası ile Para Gönder' : 'Toplu Para Gönder';
          const expectedHeaders = excelConfig.requiredProperties.join(', ');

          enqueueSnackbar(`${transactionTypeName} Excel formatı uygun değil.`, { variant: 'error' });
          setMessageType('error');
          setMessage('Excel başlıkları doğru formatta değil');
          setMessageDescription(`Beklenen başlıklar: ${expectedHeaders}. Sütun isimleri boşluklu veya boşluksuz yazılabilir.`);
        }
      };
      fileReader.readAsBinaryString(selectedFile);
    } else {
      fileInputRef.current!.value = '';
      setSpinner(false);
      setMessageType('error');
      setMessage(`Dosya formatı ${fileFormats.join(', ')} olmalıdır.`);
      setMessageDescription('Lütfen dosya formatını kontrol ediniz.');
    }
  };

  // Transaction type change
  const handleTransactionTypeChange = (typeId: string) => {
    setSelectedTransactionType(typeId);
    setListOfData([]);
    if (fileInputRef.current) fileInputRef.current.value = '';
    setMessage('');
    setMessageDescription('');
    setErrorRows([]);
    setShowErrorModal(false);
    setIsShowSendMoneyButton(true);
  };

  // Send money
  const sendMoney = async () => {
    setSpinner(true);

    // Toplam tutarları hesapla
    const totalTL = (totalValueTL || 0) + (commission?.totalCommissionTl || 0);

    // Bakiye kontrolü
    const balanceTL = balanceData?.data?.totalBalance


    let isEnough = true;
    if (totalTL > 0 && totalTL > balanceTL) isEnough = false;

    if (!isEnough) {
      setSpinner(false);
      enqueueSnackbar('Yetersiz bakiye!', { variant: 'error' });
      return;
    }

    try {
      let payload: any = {};
      if (selectedTransactionType === '48') {
        payload = {
          data: listOfData.map(item => ({
            description: item.description,
            receiverPhoneNumber: item.receiverPhoneNumber,
            currencyId: getCurrency(item.currencyId),
            value: item.value,
          })),
          companyId: currentUserState.companyId,
          customerId: currentUserState.customerId,
          type: selectedTransactionType,
        };
      } else {
        payload = {
          data: listOfData.map(item => ({
            description: item.description,
            receiverWalletNo: item.receiverWalletNo,
            currencyId: getCurrency(item.currencyId),
            value: item.value,
          })),
          companyId: currentUserState.companyId,
          customerId: currentUserState.customerId,
          type: selectedTransactionType,
        };
      }
      let response;
      if (selectedTransactionType === '48') {
        response = await sendMoneyPhoneNumberList(payload).unwrap();
      } else if (selectedTransactionType === '7') {
        response = await sendCashbackList(payload).unwrap();
      } else {
        response = await sendMoneyList(payload).unwrap();
      }

      if (response?.success) {
        setSpinner(false);
        enqueueSnackbar(response.message, { variant: 'success', autoHideDuration: 5000 });
        enqueueSnackbar('İşleminiz en kısa sürede gerçekleştirilip tarafınıza mail olarak bilgilendirme yapılacaktır', { 
          variant: 'success',
          autoHideDuration: 5000 
        });
        setIsShowSendMoneyButton(false);
        setSendMoneyAgainControl(true);
      }

    } catch (error: any) {
      setSpinner(false);
      const errorMessage = error?.data?.message || error?.message || 'İşlem sırasında hata oluştu';
      enqueueSnackbar(errorMessage, { variant: 'error' });
    }
  };

  const sendMoneyAgain = () => {
    setSelectedTransactionType('1');
    setIsShowSendMoneyButton(true);
    setSendMoneyAgainControl(false);
    setTotalValueTL(0);
    setTotalValueUSD(0);
    setTotalValueEURO(0);
    setMessage('');
    setMessageDescription('');
    setListOfData([]);
    setCommission({});
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const refreshPage = () => {
    setSelectedTransactionType('1');
    setIsShowSendMoneyButton(true);
    setSendMoneyAgainControl(false);
    setTotalValueTL(0);
    setTotalValueUSD(0);
    setTotalValueEURO(0);
    setMessage('');
    setMessageDescription('');
    setErrorRows([]);
    setShowErrorModal(false);
    setListOfData([]);
    setCommission({});
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // Excel validation - Hem boşluklu hem boşluksuz başlıkları kabul eder
  const isSendMoneyArray = (data: any[], requiredProperties?: string[]) => {
    if (!Array.isArray(data)) return false;

    // Eğer requiredProperties parametre olarak geçilmemişse varsayılan değeri kullan
    const properties = requiredProperties || [
      'Açıklama',
      'Alıcı Hesap No',
      'Para Birimi',
      'Tutar',
    ];

    // Boşluksuz alternatiflerini de içeren harita oluştur
    const propertyMap = new Map();
    properties.forEach(prop => {
      const withoutSpaces = prop.replace(/\s+/g, '');
      propertyMap.set(prop, prop); // Orijinal hali
      propertyMap.set(withoutSpaces, prop); // Boşluksuz hali
    });

    for (let i = 0; i < data.length; i++) {
      const currentHeader = data[i];
      const normalizedHeader = propertyMap.get(currentHeader);

      if (!normalizedHeader || normalizedHeader !== properties[i]) {
        return false;
      }
    }
    return true;
  };

  // Komisyon hesaplama fonksiyonu
  const calculateCommissions = async (transactionData: any[]) => {
    try {
      const payload = {
        data: transactionData.map(item => ({
          value: item.value,
          type: '1', // sabit 1 olacak cüzdan dan cüzdana
          currencyId: item.currencyId,
          companyId: currentUserState.companyId,
          customerId: currentUserState.customerId
        })),
        companyId: currentUserState.companyId,
        customerId: currentUserState.customerId
      };

      const response = await getListCommissionValue(payload).unwrap();

      // Eğer response.data doğrudan toplam komisyonları içeriyorsa
      if (response?.data) {
        setCommission({
          totalCommissionTl: response.data.totalCommissionTl || 0,
          totalCommissionUsd: response.data.totalCommissionUsd || 0,
          totalCommissionEur: response.data.totalCommissionEur || 0,
        });
      } else {
        setCommission({
          totalCommissionTl: 0,
          totalCommissionUsd: 0,
          totalCommissionEur: 0,
        });
      }
    } catch (error) {
      enqueueSnackbar('Komisyon hesaplanırken hata oluştu', { variant: 'error' });
      // Hata durumunda varsayılan komisyon değerleri
      setCommission({
        totalCommissionTl: 0,
        totalCommissionUsd: 0,
        totalCommissionEur: 0,
      });
    }
  };

  useEffect(() => {
    setTimeout(() => {
      setLoaderTrigger(true);
    }, 200);
  }, [])


  if (isInitialLoading) {

    return (
      <Container maxWidth="xl" sx={{ padding: "0px !important" }}>
        <Box
          sx={{
            minHeight: '100vh',
            background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 25%, #cbd5e1 50%, #94a3b8 75%, #64748b 100%)',
            position: 'relative',
            padding: { xs: '100px 16px 20px 16px', sm: '100px 20px 20px 20px', md: '100px 24px 20px 24px' },
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: `radial-gradient(circle at 20% 50%, rgba(255,255,255,0.1) 0%, transparent 50%),radial-gradient(circle at 80% 20%, rgba(255,255,255,0.08) 0%, transparent 50%),radial-gradient(circle at 40% 80%, rgba(255,255,255,0.06) 0%, transparent 50%),url("data:image/svg+xml,%3Csvg width=\"60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.03"%3E%3Ccircle cx="30" cy="30" r="2"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
              opacity: 0.4,
              zIndex: 0,
            },
            '&::after': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'linear-gradient(45deg, rgba(30, 60, 114, 0.02) 0%, rgba(42, 82, 152, 0.01) 50%, rgba(30, 60, 114, 0.02) 100%)',
              opacity: 0.6,
              zIndex: 0,
            },
            zIndex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Box sx={{ zIndex: 2 }}>
            <FintechLoader />
          </Box>
        </Box>
      </Container>
    );
  }

  if (loaderTrigger && !isInitialLoading && !canBulkTransferFromWallet && !canBulkTransferFromAccountToPhone) {
    return <Box sx={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center'
    }}>
      <Card sx={{
        maxWidth: 500,
        p: 4,
        borderRadius: 3,
        boxShadow: '0 8px 32px rgba(239, 68, 68, 0.1)',
        background: 'rgba(255,255,255,0.9)',
        backdropFilter: 'blur(10px)',
        border: '1px solid rgba(252, 165, 165, 0.3)',
        textAlign: 'center'
      }}>
        <SecurityIcon sx={{ fontSize: 64, color: '#ef4444', mb: 3 }} />
        <Typography variant="h5" sx={{ fontWeight: 600, color: '#dc2626', mb: 2 }}>
          Erişim Engellendi
        </Typography>
        <Typography variant="body1" sx={{ color: '#7f1d1d', lineHeight: 1.6 }}>
          Bu sayfaya erişim yetkiniz bulunmamaktadır. Sistem yöneticinizle iletişime geçiniz.
        </Typography>
      </Card>
    </Box>
  }

  return (
    <Container maxWidth="xl" sx={{ padding: "0px !important" }}>
      {/* Erişim Engellendi componenti sadece yükleme bittikten ve izinler boolean olarak kesinleşip false olduğunda gösterilsin */}


      {/* Ana İçerik - Sadece en az bir permission varsa göster */}
      {!isInitialLoading && (canBulkTransferFromWallet || canBulkTransferFromAccountToPhone) && (
        <Box sx={{
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 25%, #cbd5e1 50%, #94a3b8 75%, #64748b 100%)',
          position: 'relative',
          padding: { xs: '100px 16px 20px 16px', sm: '100px 20px 20px 20px', md: '100px 24px 20px 24px' },
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: `
          radial-gradient(circle at 20% 50%, rgba(255,255,255,0.1) 0%, transparent 50%),
          radial-gradient(circle at 80% 20%, rgba(255,255,255,0.08) 0%, transparent 50%),
          radial-gradient(circle at 40% 80%, rgba(255,255,255,0.06) 0%, transparent 50%),
          url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.03"%3E%3Ccircle cx="30" cy="30" r="2"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")
        `,
            opacity: 0.4,
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(45deg, rgba(30, 60, 114, 0.02) 0%, rgba(42, 82, 152, 0.01) 50%, rgba(30, 60, 114, 0.02) 100%)',
            opacity: 0.6,
          }
        }}>
          <Box sx={{
            maxWidth: 1200,
            mx: 'auto',
            p: { xs: 2, sm: 3, md: 4 },
            borderRadius: 6,
            boxShadow: `
          0 20px 40px -10px rgba(30, 60, 114, 0.1),
          0 8px 32px -8px rgba(0, 0, 0, 0.1),
          inset 0 1px 0 rgba(255, 255, 255, 0.2)
        `,
            backdropFilter: 'blur(20px) saturate(180%)',
            background: `
          linear-gradient(135deg, 
            rgba(255, 255, 255, 0.95) 0%, 
            rgba(255, 255, 255, 0.85) 50%, 
            rgba(248, 250, 252, 0.9) 100%
          )
        `,
            border: '1px solid rgba(255, 255, 255, 0.3)',
            mt: 2,
            position: 'relative',
            zIndex: 1,
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              borderRadius: 6,
              background: 'linear-gradient(135deg, rgba(30, 60, 114, 0.01) 0%, transparent 50%, rgba(42, 82, 152, 0.01) 100%)',
              pointerEvents: 'none',
            }
          }}>
            {/* Loader */}
            <Backdrop open={spinner} sx={{ zIndex: 9999, color: '#1976d2', backdropFilter: 'blur(4px)' }}>
              <Fade in={spinner}>
                <Stack alignItems="center" gap={2}>
                  <FintechLoader />
                  <Typography variant="h6" color="primary" fontWeight={700} letterSpacing={1}>Lütfen Bekleyiniz</Typography>
                </Stack>
              </Fade>
            </Backdrop>

            {/* Başlık */}
            <Fade in={true} timeout={500}>
              <Box sx={{ textAlign: 'center', mb: 4 }}>
                <Typography variant="h3" fontWeight={900} color="primary" sx={{ letterSpacing: 2, mb: 1, textShadow: '0 2px 8px #1976d222' }}>
                  Toplu Para Transferi
                </Typography>
                <Typography variant="h6" color="#1976d2" sx={{ opacity: 0.7 }}>
                  Lütfen yapmak istediğiniz işlem tipini seçiniz
                </Typography>
              </Box>
            </Fade>

            {/* Transaction Type Selection */}
            {!sendMoneyAgainControl && listOfData.length === 0 && (() => {
              const filteredTypes = corporateSendTypes?.filter((type: any) => {
                // Telefon numarası ile para gönderme (id=48) için permission kontrolü
                if (type.id === 48) {
                  return canBulkTransferFromAccountToPhone;
                }
                // Hesaplara Para Gönderisi (id=1) için permission kontrolü
                if (type.id === 1) {
                  return canBulkTransferFromWallet;
                }
                // Diğer tüm işlem tipleri için permission yok (hepsi görünür)
                return true;
              }) || [];

              // Eğer hiçbir işlem tipi yoksa uyarı göster
              if (filteredTypes.length === 0) {
                return (
                  <Grid container spacing={{ xs: 2, sm: 2.5, md: 3 }} justifyContent="center" mb={4}>
                    <Grid item xs={12}>
                      <Alert severity="info" sx={{ maxWidth: 800, mx: 'auto' }}>
                        <Typography variant="h6" gutterBottom>
                          Kullanılabilir İşlem Tipi Yok
                        </Typography>
                        <Typography>
                          Mevcut yetkinizle kullanabileceğiniz toplu para transferi işlem tipi bulunmuyor.
                          Lütfen sistem yöneticiniz ile iletişime geçiniz.
                        </Typography>
                      </Alert>
                    </Grid>
                  </Grid>
                );
              }

              // İşlem tiplerini göster
              return (
                <Grid container spacing={{ xs: 2, sm: 2.5, md: 3 }} justifyContent="center" mb={4}>
                  {filteredTypes.map((type: any, index: number) => (
                    <Grid item xs={12} sm={6} md={4} lg={3} key={type.id}>
                      <Fade in={true} timeout={800}>
                        <Card
                          onClick={() => handleTransactionTypeChange(type.id.toString())}
                          sx={{
                            position: 'relative',
                            cursor: 'pointer',
                            p: 3,
                            borderRadius: 4,
                            boxShadow: selectedTransactionType === type.id.toString() ? '0 4px 24px #1976d244' : '0 2px 8px #e0e0e0',
                            border: selectedTransactionType === type.id.toString() ? '2.5px solid #1976d2' : '2px solid #bdbdbd',
                            background: selectedTransactionType === type.id.toString()
                              ? 'linear-gradient(120deg, #e3f0ff 60%, #1976d2 100%)'
                              : '#f5f7fa',
                            transition: 'all 0.3s',
                            '&:hover': {
                              boxShadow: '0 8px 32px #1976d244',
                              border: '2.5px solid #1976d2',
                              background: selectedTransactionType === type.id.toString() ? undefined : '#e0e3ea',
                            },
                          }}
                        >
                          {/* Seçili kart için sabit pozisyonda onay ikonu */}
                          {selectedTransactionType === type.id.toString() && (
                            <Box sx={{
                              position: 'absolute',
                              top: 12,
                              left: 12,
                              zIndex: 2,
                              bgcolor: '#4caf50',
                              color: 'white',
                              borderRadius: '50%',
                              width: 28,
                              height: 28,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              boxShadow: '0 2px 8px rgba(76, 175, 80, 0.3)',
                            }}>
                              <CheckCircleIcon fontSize="small" />
                            </Box>
                          )}
                          <Stack direction="row" alignItems="center" gap={2}>
                            <Box sx={{
                              width: 56,
                              height: 56,
                              borderRadius: 3,
                              bgcolor: selectedTransactionType === type.id.toString() ? 'primary.main' : '#e3f0ff',
                              color: selectedTransactionType === type.id.toString() ? 'white' : 'primary.main',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              boxShadow: selectedTransactionType === type.id.toString() ? '0 2px 12px #1976d244' : 'none',
                            }}>
                              {type.id === 1 && <CreditCardIcon fontSize="large" />}
                              {type.id === 48 && <PhoneIphoneIcon fontSize="large" />}
                              {type.id === 7 && <RedeemIcon fontSize="large" />}
                              {type.id === 36 && <FastfoodIcon fontSize="large" />}
                            </Box>
                            <Box>
                              <Typography variant="h6" fontWeight={700} color={selectedTransactionType === type.id.toString() ? 'primary.main' : 'text.primary'}>
                                {type.groupName}
                              </Typography>
                            </Box>
                          </Stack>
                        </Card>
                      </Fade>
                    </Grid>
                  ))}
                </Grid>
              );
            })()}

            {/* Main Content - Only shown after selection */}
            {selectedTransactionType && (
              <Fade in={true} timeout={800}>
                <Box sx={{ width: '100%', mt: 2, cursor: 'pointer' }}>
                  {/* File Upload Section */}
                  <Grid container spacing={{ xs: 2, sm: 2.5, md: 3 }} mb={4}>
                    {!sendMoneyAgainControl && listOfData.length === 0 && (
                      <Grid item xs={12} md={6}>
                        <Card
                          sx={{
                            height: 180,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            border: selectedTransactionType ? '2px solid #1976d2' : '2px solid #e0e0e0',
                            background: 'linear-gradient(120deg, #e3f0ff 60%, #fff 100%)',
                            boxShadow: '0 2px 12px #1976d244',
                            animation: selectedTransactionType ? 'pulse 1.5s infinite' : undefined,
                          }}
                        >
                          <DownloadIcon sx={{ fontSize: 56, color: '#b5b5b5' }} />
                          <Typography mt={2} fontWeight={700} color="primary">Örnek Excel Dosyasını İndiriniz</Typography>
                          <Button variant="outlined" startIcon={<DownloadIcon />} onClick={downloadSampleFile} sx={{ mt: 2, }}>İndir</Button>
                        </Card>
                      </Grid>
                    )}
                    {!sendMoneyAgainControl && listOfData.length === 0 && (
                      <Grid item xs={12} md={6}>
                        <Card
                          sx={{
                            height: 180,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            border: '2px dashed #e0e0e0',
                            background: 'linear-gradient(120deg, #fff 60%, #e3f0ff 100%)',
                            boxShadow: '0 2px 12px #1976d244',
                          }}
                        >
                          <UploadFileIcon sx={{ fontSize: 56, color: '#b5b5b5' }} />
                          <Typography mt={2} fontWeight={700} color="primary">Dosya Yükle</Typography>
                          <input
                            type="file"
                            accept={fileFormats.join(',')}
                            onChange={handleFileChange}
                            ref={fileInputRef}
                            style={{ display: 'none' }}
                          />
                          <Button
                            variant="contained"
                            color="primary"
                            startIcon={<UploadFileIcon />}
                            sx={{ mt: 2, fontWeight: 700, letterSpacing: 1, cursor: 'pointer' }}
                            onClick={() => fileInputRef.current?.click()}
                          >Excel Yükle</Button>
                        </Card>
                      </Grid>
                    )}
                  </Grid>

                  {/* Message Display */}
                  {message && (
                    <Box mb={3}>
                      <Alert
                        severity={messageType}
                        variant="outlined"
                        sx={{
                          borderRadius: 2,
                          boxShadow: '0 2px 8px #1976d244',
                          py: 2,
                          backgroundColor: messageType === 'success' ? 'rgba(76, 175, 80, 0.08)' :
                            messageType === 'warning' ? 'rgba(255, 152, 0, 0.08)' :
                              'rgba(244, 67, 54, 0.08)',
                          borderColor: messageType === 'success' ? 'rgba(76, 175, 80, 0.3)' :
                            messageType === 'warning' ? 'rgba(255, 152, 0, 0.3)' :
                              'rgba(244, 67, 54, 0.3)'
                        }}
                        action={
                          errorRows.length > 0 && (
                            <Button
                              size="small"
                              variant="contained"
                              startIcon={<WarningAmberIcon />}
                              onClick={() => setShowErrorModal(true)}
                              sx={{
                                ml: 2,
                                fontSize: '0.8rem',
                                fontWeight: 600,
                                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                borderRadius: '20px',
                                px: 3,
                                py: 1,
                                textTransform: 'none',
                                boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)',
                                transition: 'all 0.2s ease'
                              }}
                            >
                              Hataları Görüntüle
                            </Button>
                          )
                        }
                      >
                        <Typography fontWeight={700} fontSize={18}>{message}</Typography>
                        <Typography variant="body2" sx={{ opacity: 0.8 }}>{messageDescription}</Typography>
                      </Alert>
                    </Box>
                  )}

                  {/* Data Table & Commission Info */}
                  {listOfData.length > 0 && (
                    <Card sx={{ borderRadius: 4, boxShadow: '0 4px 24px #1976d244', background: 'rgba(255,255,255,0.95)' }}>
                      {/* Commission Info */}
                      <Box sx={{ p: 3, background: 'linear-gradient(90deg, #ffebee 60%, #fff 100%)', color: '#b71c1c', borderRadius: 3 }}>
                        <Grid container alignItems="center" spacing={2}>
                          <Grid item>
                            <WarningAmberIcon fontSize="large" />
                          </Grid>
                          <Grid item xs>
                            <Typography fontWeight={900} mb={1} fontSize={20}>
                              İşlem ücretleri
                              {isCommissionLoading && (
                                <Box component="span" sx={{ ml: 1, display: 'inline-flex', alignItems: 'center', transform: 'scale(0.3)', transformOrigin: 'left center' }}>
                                  <FintechLoader />
                                </Box>
                              )}
                            </Typography>
                            <Grid container spacing={2}>
                              <Grid item xs={12} md={4}>
                                <Typography><b>Ödenecek tutar:</b> {formatAndDisplayValue(totalValueTL, 'TRY')}</Typography>
                                <Typography><b>Komisyon tutarı:</b> {formatAndDisplayValue(commission?.totalCommissionTl || 0, 'TRY')}</Typography>
                                <Typography><b>Toplam tutar:</b> {formatAndDisplayValue((totalValueTL || 0) + (commission?.totalCommissionTl || 0), 'TRY')}</Typography>
                              </Grid>
                              <Grid item xs={12} md={4}>
                                <Typography><b>Ödenecek tutar:</b> {formatAndDisplayValue(totalValueUSD, 'USD')}</Typography>
                                <Typography><b>Komisyon tutarı:</b> {formatAndDisplayValue(commission?.totalCommissionUsd || 0, 'USD')}</Typography>
                                <Typography><b>Toplam tutar:</b> {formatAndDisplayValue((totalValueUSD || 0) + (commission?.totalCommissionUsd || 0), 'USD')}</Typography>
                              </Grid>
                              <Grid item xs={12} md={4}>
                                <Typography><b>Ödenecek tutar:</b> {formatAndDisplayValue(totalValueEURO, 'EUR')}</Typography>
                                <Typography><b>Komisyon tutarı:</b> {formatAndDisplayValue(commission?.totalCommissionEur || 0, 'EUR')}</Typography>
                                <Typography><b>Toplam tutar:</b> {formatAndDisplayValue((totalValueEURO || 0) + (commission?.totalCommissionEur || 0), 'EUR')}</Typography>
                              </Grid>
                            </Grid>
                          </Grid>
                        </Grid>
                      </Box>
                      <Divider sx={{ my: 2 }} />
                      {/* Table Header & Actions */}
                      <Box sx={{ px: 4, py: 2, borderBottom: '1px solid #eee', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Typography variant="h6" fontWeight={900} color="primary">İşlemler</Typography>
                        <Box>
                          {isShowSendMoneyButton && (
                            <Button variant="contained" color="primary" onClick={sendMoney} sx={{ mr: 2, fontWeight: 700, letterSpacing: 1, boxShadow: '0 2px 8px #1976d244', cursor: 'pointer' }}>İşlemleri Başlat</Button>
                          )}
                          {!sendMoneyAgainControl && listOfData.length > 0 && (
                            <Button
                              variant="outlined"
                              color="primary"
                              startIcon={<RefreshIcon />}
                              onClick={refreshPage}
                              sx={{
                                mr: 2,
                                fontWeight: 700,
                                letterSpacing: 1,
                                cursor: 'pointer',
                                '&:hover': {
                                  transform: 'translateY(-2px)',
                                  boxShadow: '0 4px 12px #1976d244',
                                }
                              }}
                            >
                              Yenile
                            </Button>
                          )}
                          {sendMoneyAgainControl && (
                            <Button variant="contained" color="success" onClick={sendMoneyAgain} sx={{ fontWeight: 700, letterSpacing: 1, boxShadow: '0 2px 8px #43a04744', cursor: 'pointer' }}>Tekrar Para Gönder</Button>
                          )}
                        </Box>
                      </Box>
                      {/* Table */}
                      <TableContainer component={Paper} sx={{ maxHeight: 400, background: 'rgba(255,255,255,0.95)', borderRadius: 3 }}>
                        <Table stickyHeader>
                          <TableHead>
                            <TableRow sx={{ background: 'linear-gradient(90deg, #e3f0ff 60%, #fff 100%)' }}>
                              <TableCell sx={{ fontWeight: 700, color: 'primary.main' }}>Açıklama</TableCell>

                              <TableCell sx={{ fontWeight: 700, color: 'primary.main' }}>
                                {
                                  selectedTransactionType === '48' ? 'Telefon Numarası' : 'Alıcı Hesap No'
                                }
                              </TableCell>

                              <TableCell sx={{ fontWeight: 700, color: 'primary.main' }}>Para Birimi</TableCell>
                              <TableCell sx={{ fontWeight: 700, color: 'primary.main' }}>Tutar</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {listOfData.map((data, idx) => (
                              <TableRow key={idx} sx={{ '&:hover': { background: '#e3f0ff' } }}>
                                <TableCell>{data.description}</TableCell>
                                <TableCell>{
                                  selectedTransactionType === '48' ? data.receiverPhoneNumber : data.receiverWalletNo
                                }</TableCell>
                                <TableCell>{data.currencyId}</TableCell>
                                <TableCell>{formatCurrency(data.value, data.currencyId)}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Card>
                  )}
                </Box>
              </Fade>
            )}
            {/* Animation keyframes for pulse */}
            <style>{`
          @keyframes pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.5; }
          }
          .MuiCard-root[style*='animation: pulse'] {
            animation: pulse 1.5s infinite;
          }
        `}</style>
          </Box>
        </Box>
      )}

      {/* Hata Modal */}
      <Dialog
        open={showErrorModal}
        onClose={() => setShowErrorModal(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 4,
            boxShadow: '0 20px 40px rgba(0,0,0,0.15)',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            overflow: 'hidden'
          }
        }}
      >
        <DialogTitle
          sx={{
            background: 'transparent',
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: 3,
            borderBottom: '1px solid rgba(255,255,255,0.2)'
          }}
        >
          <Box display="flex" alignItems="center">
            <Box
              sx={{
                backgroundColor: 'rgba(255,255,255,0.2)',
                borderRadius: '50%',
                p: 1.5,
                mr: 2,
                backdropFilter: 'blur(10px)'
              }}
            >
              <WarningAmberIcon sx={{ fontSize: 28, color: '#ffd54f' }} />
            </Box>
            <Box>
              <Typography variant="h5" fontWeight={700} sx={{ mb: 0.5 }}>
                Veri Doğrulama Raporu
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                {errorRows.length} satırda düzeltme gerekiyor
              </Typography>
            </Box>
          </Box>
          <IconButton
            onClick={() => setShowErrorModal(false)}
            sx={{
              color: 'white',
              backgroundColor: 'rgba(255,255,255,0.1)',
              transition: 'all 0.2s ease'
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 0, backgroundColor: 'white' }}>
          <TableContainer sx={{ maxHeight: 500 }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow sx={{
                  background: 'linear-gradient(90deg, #f8f9ff 0%, #e8eaff 100%)'
                }}>
                  <TableCell sx={{
                    fontWeight: 700,
                    color: '#5a67d8',
                    fontSize: '0.9rem',
                    borderBottom: '2px solid #e2e8f0'
                  }}>
                    Satır
                  </TableCell>
                  <TableCell sx={{
                    fontWeight: 700,
                    color: '#5a67d8',
                    fontSize: '0.9rem',
                    borderBottom: '2px solid #e2e8f0'
                  }}>
                    Açıklama
                  </TableCell>
                  <TableCell sx={{
                    fontWeight: 700,
                    color: '#5a67d8',
                    fontSize: '0.9rem',
                    borderBottom: '2px solid #e2e8f0'
                  }}>
                    Alıcı
                  </TableCell>
                  <TableCell sx={{
                    fontWeight: 700,
                    color: '#5a67d8',
                    fontSize: '0.9rem',
                    borderBottom: '2px solid #e2e8f0'
                  }}>
                    Para Birimi
                  </TableCell>
                  <TableCell sx={{
                    fontWeight: 700,
                    color: '#5a67d8',
                    fontSize: '0.9rem',
                    borderBottom: '2px solid #e2e8f0'
                  }}>
                    Tutar
                  </TableCell>
                  <TableCell sx={{
                    fontWeight: 700,
                    color: '#5a67d8',
                    fontSize: '0.9rem',
                    borderBottom: '2px solid #e2e8f0'
                  }}>
                    Düzeltme Gerekli
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {errorRows.map((errorRow, idx) => (
                  <TableRow
                    key={idx}
                    sx={{
                      borderLeft: '4px solid transparent',
                      borderImage: 'linear-gradient(135deg, #667eea, #764ba2) 1',
                      transition: 'all 0.2s ease',
                      '&:nth-of-type(even)': {
                        backgroundColor: '#fafbfc'
                      }
                    }}
                  >
                    <TableCell>
                      <Box
                        sx={{
                          background: 'linear-gradient(135deg, #667eea, #764ba2)',
                          color: 'white',
                          borderRadius: '12px',
                          px: 2,
                          py: 0.5,
                          display: 'inline-flex',
                          alignItems: 'center',
                          fontWeight: 600,
                          fontSize: '0.85rem',
                          minWidth: '35px',
                          justifyContent: 'center'
                        }}
                      >
                        {errorRow.rowNumber}
                      </Box>
                    </TableCell>
                    <TableCell sx={{
                      fontSize: '0.9rem',
                      color: '#4a5568',
                      maxWidth: 150,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis'
                    }}>
                      {errorRow.description || <em style={{ color: '#a0aec0' }}>Belirtilmemiş</em>}
                    </TableCell>
                    <TableCell sx={{
                      fontSize: '0.9rem',
                      color: '#4a5568',
                      fontFamily: 'monospace'
                    }}>
                      {errorRow.receiver || <em style={{ color: '#a0aec0' }}>-</em>}
                    </TableCell>
                    <TableCell sx={{
                      fontSize: '0.9rem',
                      color: '#4a5568'
                    }}>
                      {errorRow.currency || <em style={{ color: '#a0aec0' }}>-</em>}
                    </TableCell>
                    <TableCell sx={{
                      fontSize: '0.9rem',
                      color: '#4a5568',
                      fontFamily: 'monospace'
                    }}>
                      {errorRow.amount || <em style={{ color: '#a0aec0' }}>-</em>}
                    </TableCell>
                    <TableCell>
                      <Stack spacing={0.5}>
                        {errorRow.errors.map((error: string, errorIdx: number) => (
                          <Box
                            key={errorIdx}
                            sx={{
                              background: 'linear-gradient(135deg, #fed7d7, #feb2b2)',
                              color: '#c53030',
                              borderRadius: '8px',
                              px: 1.5,
                              py: 0.5,
                              fontSize: '0.75rem',
                              fontWeight: 500,
                              display: 'inline-block',
                              border: '1px solid #fca5a5',
                              boxShadow: '0 1px 3px rgba(252, 165, 165, 0.3)'
                            }}
                          >
                            {error}
                          </Box>
                        ))}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>

        <DialogActions sx={{
          p: 3,
          background: 'linear-gradient(135deg, #f7fafc 0%, #edf2f7 100%)',
          borderTop: '1px solid #e2e8f0',
          justifyContent: 'space-between'
        }}>
          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
            💡 Hataları düzelttikten sonra dosyayı tekrar yükleyebilirsiniz
          </Typography>
          <Button
            onClick={() => setShowErrorModal(false)}
            variant="contained"
            startIcon={<CheckCircleIcon />}
            sx={{
              fontWeight: 600,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              boxShadow: '0 4px 12px rgba(102, 126, 234, 0.4)',
              borderRadius: '10px',
              px: 3,
              py: 1,
              textTransform: 'none',
              transition: 'all 0.2s ease'
            }}
          >
            Anladım
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
export default BulkTransferPage;
